'use client'

import { useCallback } from 'react'
import { useLocation } from 'react-router-dom'

import { usePermission } from '@/lib/hooks/use-permission'
import { useChatDockOptional } from '@/lib/providers/chat-dock-provider'

/** Largura mínima do dock — ele é renderizado com `hidden lg:flex`. */
const DOCK_BREAKPOINT = '(min-width: 1024px)'

/** `/atendimento/chats/123` → `123`. Qualquer outro formato devolve `null`. */
function parseChatLink(link: string | null | undefined): number | null {
    if (!link) return null
    const match = /^\/atendimento\/chats\/(\d+)(?:[/?#]|$)/.exec(link)
    if (!match) return null
    const chatId = Number(match[1])
    return Number.isFinite(chatId) ? chatId : null
}

/**
 * Abrir uma conversa POR CIMA da tela atual, quando o link apontar para uma.
 *
 * Alerta de mensagem nova chega enquanto a pessoa está no meio de outra coisa — um
 * orçamento preenchido pela metade, um filtro montado, uma rolagem longa. Navegar para
 * a tela cheia de atendimento joga tudo isso fora para responder uma linha, e o caminho
 * de volta não existe. O dock já sabe sobrepor a conversa sem navegar; aqui é só o
 * mesmo gesto a partir do alerta.
 *
 * Devolve `true` quando a sobreposição assumiu o clique. **`false` significa navegue
 * normalmente** — e isso acontece em três casos legítimos: a tela é estreita (o dock é
 * só de desktop), a pessoa já está na tela cheia de atendimento (onde o dock não é
 * renderizado, para não haver duas listas) ou ela não tem o recurso de conversas.
 * Um quarto: layout sem `ChatDockProvider` (o painel MASTER usa a mesma navbar, com
 * Meus Alertas, e não tem dock). `useChatDock` lançava ali e deixava a tela em branco.
 */
export function useChatOverlay() {
    const dock = useChatDockOptional()
    const { canRead } = usePermission()
    const location = useLocation()

    return useCallback(
        (link: string | null | undefined): boolean => {
            const chatId = parseChatLink(link)
            if (!dock || chatId === null) return false
            if (!canRead('atendimento', 'channels')) return false
            if (location.pathname.startsWith('/atendimento/chats')) return false
            if (!window.matchMedia(DOCK_BREAKPOINT).matches) return false

            // A conversa sobreposta só aparece com o dock aberto (a sobreposição é
            // posicionada a partir da borda da lista). Abrir os dois é deliberado: a
            // lista é o caminho de volta depois de responder.
            dock.openDock()
            dock.openChat(chatId)
            return true
        },
        [canRead, dock, location.pathname],
    )
}
